import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { downloadReport } from "@/lib/report";
import { useI18n } from "@/i18n";
import { AppLayout } from "@/components/AppLayout";
import { ProtectionGauge } from "@/components/ProtectionGauge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertTriangle, HelpCircle, Info, Sparkles, Loader2, Download } from "lucide-react";
import { AIText } from "@/components/AIText";

const STATUS = {
  protected: { key: "st_protected", color: "text-emerald-600 bg-emerald-50 border-emerald-100", icon: CheckCircle2 },
  review: { key: "st_review", color: "text-amber-600 bg-amber-50 border-amber-100", icon: AlertTriangle },
  gap: { key: "st_gap", color: "text-red-600 bg-red-50 border-red-100", icon: AlertTriangle },
  info: { key: "st_info", color: "text-slate-600 bg-slate-50 border-slate-100", icon: Info },
};

export default function Protection() {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/protection").then((r) => setData(r.data)); }, []);

  const getInsights = async () => {
    setLoading(true);
    try { const r = await api.get(`/protection/insights?language=${lang}`); setInsights(r.data.ai_explanation); }
    finally { setLoading(false); }
  };

  const exportPdf = () => {
    if (!data) return;
    const subs = Object.values(data.subscores).map((s) => `- ${s.label}: ${s.reason}`).join("\n");
    downloadReport({
      title: "Protection Report", language: lang,
      subtitle: `Protection Score: ${data.score}/100`,
      meta: { "Policies on file": data.policy_count, "Coverage gaps": data.gaps.length,
        ...(data.affordability_warning ? { "Deductible note": data.affordability_warning } : {}) },
      sections: [
        ...(insights ? [{ heading: "Summary", body: insights }] : []),
        { heading: "Protection map", body: subs },
        { heading: "How this score works", body: data.methodology },
      ],
    });
  };

  return (
    <AppLayout>
      <h1 className="font-display text-3xl font-semibold text-navy">{t("protection_score")}</h1>
      <p className="mt-1 text-slate-500">{t("protection_sub")}</p>
      {data && <Button data-testid="protection-export" variant="outline" onClick={exportPdf} className="mt-3 rounded-full"><Download className="h-4 w-4 mr-2" />{t("export_protection")}</Button>}

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-6 flex flex-col items-center">
          <ProtectionGauge score={data?.score || 0} size={170} />
          {data?.affordability_warning && (
            <div className="mt-4 rounded-xl bg-amber-50 border border-amber-100 p-3 text-xs text-amber-700 flex gap-2"><AlertTriangle className="h-4 w-4 shrink-0" />{data.affordability_warning}</div>
          )}
          <Button data-testid="get-insights-btn" onClick={getInsights} disabled={loading} className="mt-5 w-full rounded-full brand-gradient text-white">
            {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />{t("analyzing")}</> : <><Sparkles className="h-4 w-4 mr-2" />{t("explain_score")}</>}
          </Button>
          {insights && <div className="mt-4 rounded-xl bg-slate-50 p-4"><AIText>{insights}</AIText></div>}
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-6">
            <h2 className="text-lg font-semibold text-navy">{t("protection_map")}</h2>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {data && Object.values(data.subscores).map((s, i) => {
                const st = STATUS[s.status] || STATUS.info;
                return (
                  <div key={i} data-testid={`subscore-${s.label}`} className={`rounded-2xl border p-4 ${st.color}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-navy">{s.label}</span>
                      <span className="inline-flex items-center gap-1 text-sm font-medium"><st.icon className="h-4 w-4" />{t(st.key)}</span>
                    </div>
                    <p className="mt-1.5 text-sm text-slate-600">{s.reason}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-3xl bg-slate-50 border border-slate-100 p-6">
            <h2 className="text-sm font-semibold text-navy flex items-center gap-2"><HelpCircle className="h-4 w-4" /> {t("how_score_works")}</h2>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed">{data?.methodology}</p>
            <p className="mt-2 text-xs text-slate-400">{t("protection_planning_aid")}</p>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
